import React from 'react';
import { Wrench, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const SREAgentButton = ({ onClick, isAuthenticated, isAdmin, onLoginRequired }) => {
  // Tooltip + toasts are admin-only by design. Non-admin users see the button
  // but get no SRE-related copy on hover or click — the SRE Agent is an
  // internal operator tool and we don't want to advertise its capabilities
  // (or its risk model) to logged-out visitors or non-admin members.
  const canSeeSREDetails = isAuthenticated && isAdmin;

  const handleClick = () => {
    if (!isAuthenticated) {
      // Silent: just open the login modal. No toast.
      if (onLoginRequired) onLoginRequired();
      return;
    }
    if (!isAdmin) {
      // Silent: non-admins get no toast and no action.
      return;
    }
    toast.success('Opening SRE Agent...');
    onClick();
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            className="flex items-center gap-2 px-4 py-2 rounded-full
                       bg-gradient-to-r from-amber-500 to-orange-500
                       hover:from-amber-600 hover:to-orange-600
                       text-white font-medium transition-all duration-200
                       shadow-md hover:shadow-lg transform hover:scale-105"
            data-testid="sre-agent-button"
          >
            <Wrench className="w-4 h-4" />
            <span>SRE Agent</span>
            <Info className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        {canSeeSREDetails && (
          <TooltipContent side="bottom" className="max-w-sm bg-gray-900 text-white p-3 rounded-lg shadow-lg border border-white/10">
            <div className="space-y-2">
              <p className="font-semibold text-sm">🔧 SRE Agent</p>
              <p className="text-xs text-gray-300">
                A read-mostly site-reliability copilot. 40 hardened tools across 6 areas:
              </p>
              <ul className="text-xs text-gray-300 space-y-1 leading-snug">
                <li><span className="text-sky-400">🩺 Diagnostics</span> — service health, CPU/IO load, ps/top, host specs</li>
                <li><span className="text-sky-400">🪵 Logs</span> — tail, grep, error summaries (supervisor / nginx / app)</li>
                <li><span className="text-sky-400">🔍 Codebase</span> — find files, grep code, read source under an allowlist</li>
                <li><span className="text-sky-400">📡 Connectivity</span> — outbound TCP reachability, DNS, SSL cert expiry</li>
                <li><span className="text-sky-400">📊 App health</span> — pipeline / publisher / mongo / cron / automation status</li>
                <li><span className="text-sky-400">🛠️ Agent self</span> — config dump, audit history, recent changes, self-test</li>
                <li><span className="text-amber-300">🔧 Service control</span> — restart backend / nginx / telegram (one-click approval)</li>
                <li><span className="text-red-300">🚀 Deploy</span> — pull + redeploy (typed &quot;CONFIRM DEPLOY&quot;)</li>
              </ul>
              <div className="text-xs text-gray-300 pt-1 border-t border-white/10">
                <p className="font-medium mb-0.5">Risk model:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li><span className="text-emerald-400">LOW</span> · auto-runs, read-only</li>
                  <li><span className="text-amber-400">MEDIUM</span> · single-click approval</li>
                  <li><span className="text-red-400">HIGH</span> · typed confirmation required</li>
                </ul>
              </div>
              <p className="text-xs text-gray-300 pt-1 border-t border-white/10 leading-snug">
                🔐 <span className="font-medium">Secrets safe:</span> <code className="text-[10px]">.env</code>, <code className="text-[10px]">*.pem</code>, <code className="text-[10px]">*.key</code>, <code className="text-[10px]">id_rsa*</code>, credentials/secrets files are always blocked — even inside readable paths.
              </p>
              <p className="text-xs text-gray-300 pt-1 border-t border-white/10 leading-snug">
                📖 <span className="font-medium">Question catalogue:</span> <code className="text-[10px]">/app/docs/SRE_AGENT_QUESTION_PLAYBOOK.md</code> — full list of prompts you can ask, grouped by intent, with anti-patterns. Updated each phase.
              </p>
            </div>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

export default SREAgentButton;
